/**
 * UserPermissionEditor — super_admin-only advanced capability override editor.
 *
 * Renders an inline checklist of every capability (from the loaded matrix),
 * prefilled with the user's effective caps. The 3-state badge per row shows:
 *   • role baseline (small Check/X)
 *   • current override state (the editable checkbox)
 *   • `OVERRIDE` pill when the override differs from baseline
 *
 * Actions: Save overrides (PATCH), Reset to role defaults (DELETE). Both fire
 * to /api/admin/users/{id}/permissions (super_admin-only).
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, X, RotateCcw, Save, AlertTriangle, Loader2, Shield } from "lucide-react";
import useConfirm from "@/hooks/useConfirm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserPermissionEditor({ user, onClose, onSaved, callerRole }) {
  const [matrix, setMatrix] = useState(null);
  const [perms, setPerms] = useState(null);           // full GET response
  const [draft, setDraft] = useState({});              // local override edits
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [err, setErr] = useState(null);

  const token = localStorage.getItem("token");
  const canEdit = callerRole === "super_admin";
  const confirm = useConfirm();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [mx, p] = await Promise.all([
          axios.get(`${API}/admin/rbac/matrix`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/admin/users/${user.id}/permissions`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (!alive) return;
        setMatrix(mx.data);
        setPerms(p.data);
        setDraft(p.data.custom_permissions || {});
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Failed to load permissions");
      }
    })();
    return () => { alive = false; };
  }, [user.id, token]);

  const groups = useMemo(() => {
    if (!matrix) return {};
    const g = {};
    for (const c of matrix.capabilities) (g[c.group || "Other"] = g[c.group || "Other"] || []).push(c);
    return g;
  }, [matrix]);

  const baseline = useMemo(() => perms?.role_permissions || {}, [perms]);
  const effective = useMemo(() => {
    const out = { ...baseline };
    for (const [k, v] of Object.entries(draft || {})) out[k] = !!v;
    return out;
  }, [baseline, draft]);

  const toggleOverride = (capKey) => {
    // Clicking a cell toggles its override to the OPPOSITE of the baseline.
    // A 3rd click removes the override (back to role default).
    const cur = draft[capKey];
    if (cur === undefined) {
      // add override opposite to baseline
      setDraft({ ...draft, [capKey]: !baseline[capKey] });
    } else {
      // remove override
      const next = { ...draft };
      delete next[capKey];
      setDraft(next);
    }
  };

  const setOverrideExplicit = (capKey, value) => {
    // Force-set the override to a specific value (used by explicit +/× buttons).
    if (value === baseline[capKey]) {
      const next = { ...draft };
      delete next[capKey];
      setDraft(next);
    } else {
      setDraft({ ...draft, [capKey]: !!value });
    }
  };

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(perms?.custom_permissions || {}), [draft, perms]);

  const save = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/admin/users/${user.id}/permissions`,
        { custom_permissions: draft },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPerms(res.data);
      setDraft(res.data.custom_permissions || {});
      toast.success("Permission overrides saved");
      onSaved?.(res.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "invalid_capability_keys") toast.error(`Unknown capability key(s): ${d.unknown.join(", ")}`);
      else toast.error(typeof d === "string" ? d : "Save failed");
    } finally { setSaving(false); }
  };

  const resetAll = async () => {
    if (!canEdit) return;
    const ok = await confirm({
      title: "Reset to role defaults?",
      body: `This clears every override for ${user.full_name || user.email} and returns them to the baseline capabilities of their role.`,
      confirmLabel: "Reset overrides",
      confirmTone: "danger",
      testid: "user-permissions-reset-confirm",
    });
    if (!ok) return;
    setResetting(true);
    try {
      const res = await axios.delete(`${API}/admin/users/${user.id}/permissions`, { headers: { Authorization: `Bearer ${token}` } });
      setPerms(res.data);
      setDraft({});
      toast.success("Overrides cleared — user is back on role defaults");
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setResetting(false); }
  };

  if (err) return <div data-testid="permission-editor-error" style={{ padding: 10, background: "#FFEBEB", color: "#A32D2D", fontSize: 12, borderRadius: 8 }}>{err}</div>;
  if (!matrix || !perms) return <div data-testid="permission-editor-loading" style={{ padding: 20, color: "#888", fontSize: 12 }}><Loader2 className="animate-spin" style={{ display: "inline", width: 12, height: 12, marginRight: 6 }} /> Loading permissions…</div>;

  const overrideCount = Object.keys(draft).length;
  const gainedCount = Object.entries(draft).filter(([k, v]) => v && !baseline[k]).length;
  const lostCount = Object.entries(draft).filter(([k, v]) => !v && baseline[k]).length;

  return (
    <div data-testid="permission-editor">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 650, display: "flex", alignItems: "center", gap: 6 }}>
            <Shield style={{ width: 14, height: 14, color: "#2563eb" }} /> Advanced permission overrides
          </div>
          <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 2 }}>
            Role baseline: <strong>{perms.role}</strong> · overrides modify individual capabilities without changing the role.
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {overrideCount > 0 && (
            <span data-testid="permission-editor-override-count" style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 8, background: "#FFF8F0", color: "#854F0B" }}>
              {overrideCount} override{overrideCount === 1 ? "" : "s"}
              {gainedCount ? ` · +${gainedCount}` : ""}{lostCount ? ` · −${lostCount}` : ""}
            </span>
          )}
        </div>
      </div>

      {!canEdit && (
        <div data-testid="permission-editor-readonly-banner" style={{ background: "#EEF4FF", border: "0.5px solid #C0D3F7", borderRadius: 8, padding: "8px 12px", fontSize: 11, color: "#2563eb", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
          <AlertTriangle style={{ width: 12, height: 12 }} />
          Only <strong>super_admin</strong> can edit per-user overrides. You can review, but not save changes.
        </div>
      )}

      <div style={{ maxHeight: 420, overflow: "auto", border: "0.5px solid var(--border)", borderRadius: 8 }}>
        {Object.entries(groups).map(([g, caps]) => (
          <div key={g}>
            <div style={{ padding: "6px 12px", fontSize: 10, fontWeight: 700, color: "#2563eb", background: "#f9faff", textTransform: "uppercase", letterSpacing: 0.4 }}>{g}</div>
            {caps.map(c => {
              const base = !!baseline[c.key];
              const override = draft[c.key];
              const hasOverride = override !== undefined;
              const eff = hasOverride ? override : base;
              return (
                <div
                  key={c.key}
                  data-testid={`permission-editor-row-${c.key}`}
                  data-has-override={hasOverride}
                  data-effective={eff}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 12px", borderBottom: "0.5px solid var(--border)", fontSize: 11, background: hasOverride ? "#FFF8F0" : "transparent" }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                    <span title={c.description} style={{ fontWeight: 600, color: "var(--text-primary)" }}>{c.label || c.key}</span>
                    {(c.flags || []).includes("admin") && <span style={{ background: "#FFF8F0", color: "#854F0B", padding: "0 5px", borderRadius: 6, fontSize: 9, fontWeight: 700 }}>ADMIN</span>}
                    <code style={{ fontSize: 10, color: "#888" }}>{c.key}</code>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 9, color: "#888" }}>base:</span>
                    {base ? <Check style={{ width: 11, height: 11, color: "#0F6E56" }} /> : <X style={{ width: 11, height: 11, color: "#bbb" }} />}
                    <div style={{ display: "flex", alignItems: "center", gap: 2, marginLeft: 6 }}>
                      <button
                        data-testid={`permission-editor-grant-${c.key}`}
                        onClick={() => setOverrideExplicit(c.key, true)}
                        disabled={!canEdit}
                        aria-label={`grant ${c.label}`}
                        title="Grant"
                        style={{ border: "0.5px solid var(--border)", padding: "2px 6px", background: eff ? "#DAFBE8" : "transparent", color: eff ? "#0F6E56" : "#888", borderRadius: 4, cursor: canEdit ? "pointer" : "not-allowed", fontSize: 10 }}
                      >✓</button>
                      <button
                        data-testid={`permission-editor-revoke-${c.key}`}
                        onClick={() => setOverrideExplicit(c.key, false)}
                        disabled={!canEdit}
                        aria-label={`revoke ${c.label}`}
                        title="Revoke"
                        style={{ border: "0.5px solid var(--border)", padding: "2px 6px", background: !eff ? "#FFEBEB" : "transparent", color: !eff ? "#A32D2D" : "#888", borderRadius: 4, cursor: canEdit ? "pointer" : "not-allowed", fontSize: 10 }}
                      >×</button>
                      {hasOverride && (
                        <button
                          data-testid={`permission-editor-clear-${c.key}`}
                          onClick={() => toggleOverride(c.key)}
                          disabled={!canEdit}
                          aria-label={`reset ${c.label} to role default`}
                          title="Reset to role default"
                          style={{ border: "0.5px solid var(--border)", padding: "2px 6px", background: "transparent", color: "#666", borderRadius: 4, cursor: canEdit ? "pointer" : "not-allowed", fontSize: 10 }}
                        ><RotateCcw style={{ width: 10, height: 10 }} /></button>
                      )}
                    </div>
                    {hasOverride && (
                      <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 6, background: override ? "#DAFBE8" : "#FFEBEB", color: override ? "#0F6E56" : "#A32D2D", marginLeft: 4 }}>
                        {override ? "OVERRIDE +" : "OVERRIDE −"}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, gap: 8 }}>
        <button
          data-testid="permission-editor-reset"
          onClick={resetAll}
          disabled={!canEdit || resetting || !perms.has_custom_permissions}
          className="btn"
          style={{ fontSize: 12, opacity: (!canEdit || !perms.has_custom_permissions) ? 0.4 : 1 }}
        >
          <RotateCcw style={{ width: 12, height: 12 }} /> Reset to role defaults
        </button>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} className="btn" style={{ fontSize: 12 }} data-testid="permission-editor-close">Close</button>
          <button
            data-testid="permission-editor-save"
            onClick={save}
            disabled={!canEdit || saving || !dirty}
            className="btn btn-primary"
            style={{ fontSize: 12, opacity: (!canEdit || !dirty) ? 0.4 : 1 }}
          >
            <Save style={{ width: 12, height: 12 }} /> {saving ? "Saving…" : "Save overrides"}
          </button>
        </div>
      </div>
    </div>
  );
}
